import React, { useId } from "react";
import { getLocaleLabel } from "../config/resolveNavigation.js";

/**
 * @param {object} props
 * @param {{ id: string, label: string, icon?: string }} props.role
 * @param {string} [props.testId]
 */
export function GsdRoleBadge({ role, testId = "gsd-role-badge" }) {
  if (!role?.label) return null;
  return (
    <span className="gsd-role-badge" data-testid={testId}>
      {role.icon ? <span className="gsd-role-badge__icon" aria-hidden="true">{role.icon}</span> : null}
      <span className="gsd-role-badge__label">{role.label}</span>
    </span>
  );
}

/**
 * @param {Array<{ id: string, label?: string, labelFr?: string, labelEn?: string, icon?: string }>} roles
 * @param {'fr'|'en'|string} locale
 */
function normalizeRoles(roles, locale) {
  return roles.map((r) => ({
    ...r,
    label: r.label || getLocaleLabel(r, locale),
  }));
}

/**
 * Demo / multi-role switcher — dropdown "Mode" (GolfMAP pattern) by default.
 * @param {object} props
 * @param {Array<{ id: string, label?: string, labelFr?: string, labelEn?: string, icon?: string }>} props.roles
 * @param {string} props.activeRoleId
 * @param {(roleId: string) => void} [props.onRoleChange]
 * @param {'fr'|'en'|string} [props.locale]
 * @param {string} [props.modeLabel]
 * @param {boolean} [props.compact]
 * @param {'dropdown'|'pills'} [props.variant]
 * @param {string} [props.switchLabel]
 * @param {string} [props.testId]
 */
export function GsdRoleSwitcher({
  roles = [],
  activeRoleId,
  onRoleChange,
  locale = "fr",
  modeLabel,
  compact = false,
  variant = "dropdown",
  switchLabel = "Changer",
  testId = "gsd-role-switcher",
}) {
  const selectId = useId();
  const normalizedRoles = normalizeRoles(roles, locale);
  const active = normalizedRoles.find((r) => r.id === activeRoleId) || { id: activeRoleId, label: activeRoleId };
  const labelMode = modeLabel || (locale === "en" ? "Mode" : "Mode");
  const ariaLabel = locale === "en" ? "Display mode" : "Mode d'affichage";
  const canSwitch = Boolean(onRoleChange) && normalizedRoles.length > 1;
  const compactClass = compact ? " gsd-role-switcher--compact" : "";

  if (!canSwitch) {
    return (
      <div className={`gsd-role-switcher gsd-role-switcher--readonly${compactClass}`} data-testid={testId}>
        <span className="gsd-role-switcher__label">{labelMode}</span>
        <GsdRoleBadge role={active} testId="gsd-role-switcher-badge" />
        {!onRoleChange && switchLabel ? <span className="gsd-role-switcher__hint">{switchLabel}</span> : null}
      </div>
    );
  }

  if (variant === "pills") {
    return (
      <div className={`gsd-role-switcher gsd-role-switcher--pills${compactClass}`} data-testid={testId}>
        <GsdRoleBadge role={active} testId="gsd-role-switcher-badge" />
        <div className="gsd-role-switcher__options">
          {normalizedRoles
            .filter((r) => r.id !== activeRoleId)
            .map((r) => (
              <button
                key={r.id}
                type="button"
                className="gsd-role-switcher__option"
                onClick={() => onRoleChange?.(r.id)}
                data-testid={`gsd-role-switch-${r.id}`}
              >
                {r.icon ? <span aria-hidden="true">{r.icon}</span> : null}
                {r.label}
              </button>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`gsd-role-switcher gsd-role-switcher--dropdown${compactClass}`} data-testid={testId}>
      <label htmlFor={selectId} className="gsd-role-switcher__label">
        {labelMode}
      </label>
      <select
        id={selectId}
        className="gsd-role-switcher__select"
        value={activeRoleId}
        onChange={(e) => onRoleChange?.(e.target.value)}
        aria-label={ariaLabel}
        data-testid="gsd-role-switch-select"
      >
        {normalizedRoles.map((r) => (
          <option key={r.id} value={r.id}>
            {r.icon ? `${r.icon} ` : ""}
            {r.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * Role home layout with configurable slots (header, bulletin, actions, stats, content).
 * Apps branch modules into these slots — they do not position them with local CSS.
 * @param {object} props
 * @param {React.ReactNode} [props.header]
 * @param {React.ReactNode} [props.bulletin]
 * @param {React.ReactNode} [props.quickActions]
 * @param {React.ReactNode} [props.stats]
 * @param {React.ReactNode} props.children
 * @param {string} [props.testId]
 */
export function GsdRoleHomeLayout({
  header,
  bulletin,
  quickActions,
  stats,
  children,
  testId = "gsd-role-home",
}) {
  return (
    <div className="gsd-role-home" data-testid={testId}>
      {header ? <div className="gsd-role-home__slot gsd-role-home__slot--header">{header}</div> : null}
      {quickActions ? (
        <div className="gsd-role-home__slot gsd-role-home__slot--actions">{quickActions}</div>
      ) : null}
      {stats ? <div className="gsd-role-home__slot gsd-role-home__slot--stats">{stats}</div> : null}
      <div className="gsd-role-home__content">{children}</div>
      {bulletin ? (
        <div className="gsd-role-home__slot gsd-role-home__slot--bulletin" data-testid="gsd-home-slot-bulletin">
          {bulletin}
        </div>
      ) : null}
    </div>
  );
}
